// frontend/src/contexts/PricingContext.js

import React, {
  createContext,
  startTransition,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import api from '../services/api';

const PricingContext = createContext();

// Hand-maintained MIRROR of the backend's prices. It seeds state, so it is what
// every visitor paints on first load - not only an API-down path - and it must
// therefore agree with `settings.PAYMENT_AMOUNT_*`.
//
// Nothing keeps the two in sync automatically. A backend test
// (backend/tests/test_solo_price_single_source.py) parses THIS FILE and fails if
// a number drifts, and this file is named individually in the `backend` paths
// filter of .github/workflows/test.yml so that guard actually runs when only the
// frontend changes. Renaming or moving this file means updating both.
//
// Replacing these mirrors with a generated file is future_developments.md #167.
const FALLBACK_PRICING = {
  tiers: {
    trial: {
      name: 'Trial',
      price_monthly: 0,
      price_display: 'Free',
      currency: 'ILS'
    },
    solo: {
      name: 'Solo',
      price_monthly: 199,
      price_display: '199',
      currency: 'ILS'
    },
    professional: {
      name: 'Professional',
      price_monthly: 299,
      price_display: '299',
      currency: 'ILS'
    },
    enterprise: {
      name: 'Enterprise',
      price_monthly: null,
      price_display: 'Contact Us',
      currency: 'ILS'
    }
  }
};

// The offline plan prices as they DISPLAY, derived from the mirror above so a
// non-React consumer can state a price without minting another copy of the
// number. Read by the homepage FAQ answer and the SoftwareApplication JSON-LD -
// both of which must render with no network, which is exactly what this mirror
// is for. Never re-type a price at a call site; import this.
export const PLAN_PRICE_DISPLAY = {
  solo: FALLBACK_PRICING.tiers.solo.price_display,
  professional: FALLBACK_PRICING.tiers.professional.price_display,
};

// The free-trial length, in days. A hand-maintained MIRROR of the backend's
// `tier_config.TRIAL_DURATION_DAYS`, and it lives HERE rather than beside the
// copy that quotes it for one reason: this file is named individually in
// test.yml's `backend` paths filter, so the guard in
// backend/tests/test_solo_price_single_source.py that compares it against
// `get_trial_duration()` actually runs on a frontend-only PR. Declared in
// faqData.js until 2026-09-06, where nothing could see it drift.
// There is no `GET /pricing` field for it, so unlike the prices this mirror is
// never corrected by the API - what is written here is what every visitor
// reads, forever.
export const FREE_TRIAL_DAYS = 14;

// How many professions the Professional plan follows at once. A hand-maintained
// MIRROR of the backend's `tier_config['professional']['max_categories']`, kept
// HERE for the same reason as FREE_TRIAL_DAYS: this file is named in test.yml's
// `backend` paths filter, so
// backend/tests/test_professional_profession_count_single_source.py compares it
// against tier_config on a frontend-only PR. Quoted by the shared features tile
// (components/homepage/FeaturesLive.js) on the homepage and every profession
// page - import it, never re-type the digit. (PricingSection.js and Payment.js
// still spell the digit inside their offline card bullets and chip keys; the
// same backend test pins those strings directly.)
export const PROFESSIONAL_MAX_CATEGORIES = 4;

export const PricingProvider = ({ children }) => {
  const [pricing, setPricing] = useState(FALLBACK_PRICING);
  const [loading, setLoading] = useState(true);

  // The reply's state updates are TRANSITIONS (#464). They reach every
  // usePricing() consumer - on a prerendered page possibly while the route is
  // still being hydrated - and an urgent context update arriving there makes
  // React 19 give up on that boundary and show the loading fallback over the
  // snapshot; a transition lets the hydration finish first. src/index.js
  // already loads the route's page before it hydrates, so this is the second
  // line, not the fix. Nothing else changes: the same two updates land in the
  // same render (the prices on screen come from FALLBACK_PRICING until then,
  // exactly as before).
  useEffect(() => {
    const fetchPricing = async () => {
      let data = null;
      try {
        const response = await api.get('/pricing');
        data = response.data;
      } catch (error) {
        console.warn('Failed to fetch pricing, using fallback:', error);
        // Keep FALLBACK_PRICING already set in state
      }
      startTransition(() => {
        if (data !== null) setPricing(data);
        setLoading(false);
      });
    };

    fetchPricing();
  }, []);

  // Memoized: an inline value object re-rendered every usePricing() consumer
  // on each provider render (context bypasses React.memo).
  const value = useMemo(() => ({ pricing, loading }), [pricing, loading]);

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within PricingProvider');
  }
  return context;
};
